// Builder presets from analyticax-prototype_0929.html.
// General templates are always shown. Industry templates appear for the selected industry.

export const MASTER_INDUSTRIES = [
  { value: 'education', label: 'Education / Schools' },
  { value: 'healthcare', label: 'Healthcare / Clinics' },
  { value: 'retail', label: 'Retail / Inventory-led' },
];

const TYPE_TO_FORM = {
  Text: 'text',
  Number: 'integer',
  Date: 'date',
  Dropdown: 'enum',
  'Yes / no': 'boolean',
  Attachment: 'attachment',
  Reference: 'reference',
};

const REF_NAME_HINTS = {
  students: ['student'],
  employees: ['employee', 'staff'],
};

function field(name, type, extras = {}) {
  return { name, type, ...extras };
}

const TEMPLATE_PRESETS = [
  {
    id: 'blank',
    scope: 'both',
    industry: 'general',
    label: 'Blank',
    desc: 'Start from scratch with a single empty field.',
    fields: [field('', 'Text')],
  },
  {
    id: 'hr_onboarding',
    scope: 'operational',
    industry: 'general',
    label: 'HR onboarding',
    desc: 'New-hire checklist, linked to Employees.',
    fields: [
      field('Employee', 'Reference', { ref: 'employees', searchable: true }),
      field('Offer sent', 'Yes / no'),
      field('Docs verified', 'Yes / no'),
      field('Start date', 'Date'),
      field('Status', 'Dropdown', { searchable: true }),
    ],
  },
  {
    id: 'staff_directory',
    scope: 'master',
    industry: 'general',
    label: 'Staff directory',
    desc: 'Master employee list with auto-generated employee IDs.',
    fields: [
      field('Full name', 'Text', { dedupe: true, searchable: true }),
      field('Department', 'Dropdown', { searchable: true }),
      field('Designation', 'Dropdown', { searchable: true }),
      field('Gender', 'Dropdown'),
      field('Date of joining', 'Date'),
      field('Active', 'Yes / no'),
      field('Contact number', 'Text'),
      field('Email', 'Text'),
      field('Qualification', 'Text'),
      field('Address', 'Text'),
      field('Employee ID', 'Text', { dedupe: true, seq: true, seqPrefix: 'EMP-', seqStart: 1001 }),
    ],
  },
  {
    id: 'attendance',
    scope: 'operational',
    industry: 'education',
    label: 'Student attendance',
    desc: 'Daily attendance and leave log, linked to Students.',
    fields: [
      field('Student', 'Reference', { ref: 'students', searchable: true }),
      field('Class', 'Dropdown'),
      field('Date', 'Date'),
      field('Status', 'Dropdown', { searchable: true }),
      field('Is late', 'Yes / no'),
      field('Leave type', 'Dropdown'),
      field('Leave from', 'Date'),
      field('Leave to', 'Date'),
      field('Approval status', 'Dropdown', { searchable: true }),
      field('Remarks', 'Text'),
    ],
  },
  {
    id: 'academics',
    scope: 'operational',
    industry: 'education',
    label: 'Academics',
    desc: 'Subject-wise exam results, linked to Students.',
    fields: [
      field('Student', 'Reference', { ref: 'students', searchable: true }),
      field('Class', 'Dropdown'),
      field('Subject', 'Dropdown', { searchable: true }),
      field('Term', 'Dropdown'),
      field('Exam type', 'Dropdown'),
      field('Test name', 'Text'),
      field('Test date', 'Date'),
      field('Marks', 'Number'),
      field('Max marks', 'Number'),
      field('Grade', 'Text', { searchable: true }),
    ],
  },
  {
    id: 'fees',
    scope: 'operational',
    industry: 'education',
    label: 'Receivables',
    desc: 'Fee dues and payment collection per student.',
    fields: [
      field('Student', 'Reference', { ref: 'students', searchable: true }),
      field('Fee type', 'Dropdown', { searchable: true }),
      field('Installment name', 'Text'),
      field('Due date', 'Date'),
      field('Installment amount', 'Number'),
      field('Concession', 'Number'),
      field('Fine amount', 'Number'),
      field('Paid amount', 'Number'),
      field('Paid date', 'Date'),
      field('Payment mode', 'Dropdown'),
    ],
  },
  {
    id: 'admissions',
    scope: 'operational',
    industry: 'education',
    label: 'Admissions',
    desc: 'Enquiry-to-admission pipeline, before a student joins the roster.',
    fields: [
      field('Student name', 'Text', { searchable: true }),
      field('Class applied for', 'Dropdown'),
      field('Enquiry date', 'Date'),
      field('Source', 'Dropdown', { searchable: true }),
      field('Status', 'Dropdown', { searchable: true }),
      field('Stage', 'Dropdown'),
      field('Follow-up date', 'Date'),
      field('Application no', 'Text', { dedupe: true }),
      field('Application date', 'Date'),
      field('Counseled by', 'Text'),
    ],
  },
  {
    id: 'students_roster',
    scope: 'master',
    industry: 'education',
    label: 'Students roster',
    desc: 'Master student list with auto-generated enrolment IDs.',
    fields: [
      field('Full name', 'Text', { dedupe: true, searchable: true }),
      field('Class', 'Dropdown', { searchable: true }),
      field('Section', 'Dropdown', { searchable: true }),
      field('Academic year', 'Dropdown'),
      field('Gender', 'Dropdown'),
      field('Date of birth', 'Date'),
      field('Active', 'Yes / no'),
      field('Father’s name', 'Text'),
      field('Mother’s name', 'Text'),
      field('Contact number', 'Text'),
      field('Address', 'Text'),
      field('Student enrolment ID', 'Text', { dedupe: true, seq: true, seqPrefix: 'ENR-', seqStart: 1001 }),
    ],
  },
  {
    id: 'patients_roster',
    scope: 'master',
    industry: 'healthcare',
    label: 'Patients roster',
    desc: 'Master patient list with auto-generated patient IDs.',
    fields: [
      field('Full name', 'Text', { dedupe: true, searchable: true }),
      field('Date of birth', 'Date'),
      field('Patient ID', 'Text', { dedupe: true, seq: true, seqPrefix: 'PAT-', seqStart: 1001 }),
    ],
  },
  {
    id: 'doctors_directory',
    scope: 'master',
    industry: 'healthcare',
    label: 'Doctors directory',
    desc: 'Master doctor list with auto-generated doctor IDs.',
    fields: [
      field('Full name', 'Text', { dedupe: true, searchable: true }),
      field('Specialization', 'Dropdown', { searchable: true }),
      field('Doctor ID', 'Text', { dedupe: true, seq: true, seqPrefix: 'DOC-', seqStart: 1001 }),
    ],
  },
  {
    id: 'patient_visits',
    scope: 'operational',
    industry: 'healthcare',
    label: 'Patient visits',
    desc: 'Visit log — link the Patient field to your Patients roster once created.',
    fields: [
      field('Patient name', 'Text', { searchable: true }),
      field('Visit date', 'Date'),
      field('Diagnosis', 'Text'),
      field('Status', 'Dropdown', { searchable: true }),
    ],
  },
  {
    id: 'prescriptions',
    scope: 'operational',
    industry: 'healthcare',
    label: 'Prescriptions',
    desc: 'Medication log — link the Patient field to your Patients roster once created.',
    fields: [
      field('Patient name', 'Text', { searchable: true }),
      field('Medication', 'Text', { searchable: true }),
      field('Dosage', 'Text'),
      field('Status', 'Dropdown'),
    ],
  },
  {
    id: 'products_catalog',
    scope: 'master',
    industry: 'retail',
    label: 'Products catalog',
    desc: 'Master product list with auto-generated SKUs.',
    fields: [
      field('Product name', 'Text', { dedupe: true, searchable: true }),
      field('Category', 'Dropdown', { searchable: true }),
      field('SKU', 'Text', { dedupe: true, seq: true, seqPrefix: 'SKU-', seqStart: 1001 }),
    ],
  },
  {
    id: 'vendor_list',
    scope: 'master',
    industry: 'retail',
    label: 'Vendor list',
    desc: 'Supplier master with auto-generated vendor IDs.',
    fields: [
      field('Vendor name', 'Text', { dedupe: true, searchable: true }),
      field('Category', 'Dropdown'),
      field('Vendor ID', 'Text', { dedupe: true, seq: true, seqPrefix: 'VEN-', seqStart: 1001 }),
    ],
  },
  {
    id: 'inventory',
    scope: 'operational',
    industry: 'retail',
    label: 'Inventory',
    desc: 'Stock levels and reorder tracking — link Product to your catalog once created.',
    fields: [
      field('Product name', 'Text', { dedupe: true, searchable: true }),
      field('Quantity', 'Number'),
      field('Reorder point', 'Number'),
    ],
  },
  {
    id: 'sales_orders',
    scope: 'operational',
    industry: 'retail',
    label: 'Sales orders',
    desc: 'Customer orders — link Product to your catalog once created.',
    fields: [
      field('Product name', 'Text', { searchable: true }),
      field('Customer name', 'Text', { searchable: true }),
      field('Quantity', 'Number'),
      field('Order date', 'Date'),
      field('Status', 'Dropdown', { searchable: true }),
    ],
  },
  {
    id: 'purchase_orders',
    scope: 'operational',
    industry: 'retail',
    label: 'Purchase orders',
    desc: 'Orders placed with vendors — link Vendor to your vendor list once created.',
    fields: [
      field('Vendor name', 'Text', { searchable: true }),
      field('Item', 'Text', { searchable: true }),
      field('Quantity', 'Number'),
      field('Order date', 'Date'),
      field('Status', 'Dropdown'),
    ],
  },
];

function templatesFor(industry, scope) {
  return TEMPLATE_PRESETS.filter(
    (template) =>
      (template.scope === scope || template.scope === 'both') &&
      (template.industry === 'general' || template.industry === industry)
  );
}

export function getMasterTemplates(industry) {
  return templatesFor(industry, 'master');
}

export function getPageTemplates(industry) {
  return templatesFor(industry, 'operational');
}

function compactTeachingField(item) {
  const mapped = {
    name: item.name,
    type: TYPE_TO_FORM[item.type] || 'text',
  };
  if (item.ref) mapped.ref_hint = item.ref;
  if (item.dedupe) mapped.dedupe = true;
  if (item.searchable) mapped.search = true;
  if (item.seq) {
    mapped.seq = true;
    mapped.seqPrefix = item.seqPrefix || '';
    mapped.seqStart = item.seqStart || 1001;
  }
  return mapped;
}

/** Standard templates used as few-shot examples for AI drafts. Blank is excluded. */
export function getAiTeachingExamples(industry, mode) {
  const scope = mode === 'master' ? 'master' : 'operational';
  return templatesFor(industry, scope)
    .filter((template) => template.id !== 'blank' && template.fields.some((item) => item.name))
    .map((template) => ({
      label: template.label,
      desc: template.desc,
      fields: template.fields.filter((item) => item.name).map(compactTeachingField),
    }));
}

function linkedMasterId(refKey, masterOptions) {
  if (!refKey) return '';
  const hints = REF_NAME_HINTS[refKey] || [String(refKey).toLowerCase()];
  const match = (masterOptions || []).find((option) => {
    const label = String(option.label || '').toLowerCase();
    return hints.some((hint) => label.includes(hint));
  });
  return match ? String(match.id) : '';
}

export function templateFieldCount(template) {
  return template.fields.filter((item) => item.name).length;
}

export function templateToColumns(template, masterOptions = []) {
  return template.fields.map((item) => ({
    name: item.name || '',
    type: TYPE_TO_FORM[item.type] || 'text',
    required: false,
    ref_schema_id: item.type === 'Reference' ? linkedMasterId(item.ref, masterOptions) : '',
    dedupe: item.dedupe === true,
    search: item.searchable === true,
    seq: item.seq === true,
    seqPrefix: item.seqPrefix || '',
    seqStart: item.seqStart || 1,
  }));
}
